import { useEffect, useRef, useState } from 'react';

interface MuseVideoPlayerProps {
  videoId: string;
  className?: string;
  width?: string;
  height?: string;
}

const MuseVideoPlayer = ({ 
  videoId, 
  className = "", 
  width = "100%",
  height = "auto" 
}: MuseVideoPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    
    const loadScript = () => {
      // Check if script already exists
      const existingScript = document.querySelector('script[src="https://skiv.com/static/js/embed-player.min.js"]');
      
      if (existingScript) {
        // Script exists, just wait a moment for it to process
        console.log('Muse.ai script already loaded, waiting for processing...');
        timeoutId = setTimeout(() => {
          setIsLoading(false);
        }, 2000);
        return;
      }

      // Create and load script
      console.log('Loading Muse.ai script...');
      const script = document.createElement('script');
      script.src = 'https://skiv.com/static/js/embed-player.min.js';
      script.async = true;
      
      script.onload = () => {
        console.log('Muse.ai script loaded successfully');
        // Give the script time to process all muse-video-player elements
        timeoutId = setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      };
      
      script.onerror = () => {
        console.error('Failed to load Muse.ai script');
        setError('Failed to load video player');
        setIsLoading(false);
      };
      
      document.head.appendChild(script);
    };

    loadScript();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  if (error) {
    return (
      <div className={`bg-gray-900 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-white text-center p-8">
          <p className="mb-4">Video failed to load</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-blue-400 hover:text-blue-300 underline"
          >
            Refresh page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={className}>
      <div 
        className="muse-video-player" 
        data-video={videoId}
        data-search="0" 
        data-links="0" 
        data-logo="0" 
        data-title="0" 
        data-autoplay="1"
        data-volume="0"
        data-coverplayposition="center" 
        data-width={width}
        data-height={height}
      />
      {isLoading && (
        <div className="absolute inset-0 bg-gray-900 rounded-lg flex items-center justify-center">
          <div className="text-white">Loading video...</div>
        </div>
      )}
    </div>
  );
};

export default MuseVideoPlayer;